// Teams screen - view all teams and their gladiators

import { Canvas } from '../Canvas';
import { CONSTANTS, ScreenState } from '../types';
import { Screen, ScreenContext } from './Screen';
import { GameState, getCurrentTeam } from '../systems/GameState';
import { TeamData } from '../entities/Team';
import { ScrollableList } from '../components/ScrollableList';

export class TeamsScreen implements Screen {
  readonly state = ScreenState.OTHER_TEAMS;
  private context: ScreenContext;
  private gameState: GameState | null = null;
  private selectedGladiatorIndex: number = 0;
  private teamList: ScrollableList<TeamData>;

  constructor(context: ScreenContext) {
    this.context = context;
    this.teamList = new ScrollableList<TeamData>({
      x: 20,
      y: 60,
      width: 300,
      height: 500,
      itemHeight: 40,
      header: 'Joukkueet',
      style: {
        backgroundColor: '#ffffff',
        borderColor: CONSTANTS.TABLE_BORDER,
        headerColor: '#000000',
        selectedColor: CONSTANTS.SELECTED_GLADIATOR,
        scrollButtonColor: '#4466aa',
        scrollButtonDisabledColor: '#aaaaaa',
        scrollButtonTextColor: '#ffffff',
        positionTextColor: '#666666',
      },
      renderItem: (canvas, team, x, y, _width, _height, _isSelected) => {
        const isPlayerTeam = this.gameState?.playerTeamIds.includes(team.id) ?? false;
        const nameColor = isPlayerTeam ? '#006600' : '#000000';

        canvas.drawText(team.name, x + 10, y + 5, CONSTANTS.MAIN_FONT, nameColor);
        canvas.drawText(
          `Div ${team.division + 1} | ${team.gladiatorIds.length} glad. | ${team.wins} V`,
          x + 10, y + 22, CONSTANTS.MSG_FONT, '#666666'
        );
      },
    });
  }

  setGameState(gameState: GameState): void {
    this.gameState = gameState;
  }

  enter(): void {
    console.log('Entering TeamsScreen');
    this.updateTeamList();
    this.teamList.reset();
    this.selectedGladiatorIndex = 0;
  }

  private updateTeamList(): void {
    if (!this.gameState) return;

    const teams = Array.from(this.gameState.teams.values())
      .sort((a, b) => a.division - b.division || a.name.localeCompare(b.name));

    this.teamList.setItems(teams.map(team => ({ id: team.id, data: team })));
  }

  exit(): void {
    console.log('Exiting TeamsScreen');
  }

  update(_deltaTime: number): void {}

  render(canvas: Canvas): void {
    if (!this.gameState) {
      canvas.clear(CONSTANTS.MAIN_BACKGROUND);
      return;
    }

    canvas.clear(CONSTANTS.MAIN_BACKGROUND);

    // Header
    this.renderHeader(canvas);

    // Teams list (using ScrollableList component)
    this.teamList.render(canvas);

    // Selected team details
    this.renderTeamDetails(canvas);

    // Back button
    this.renderBackButton(canvas);
  }

  private renderHeader(canvas: Canvas): void {
    canvas.drawRect(0, 0, CONSTANTS.CANVAS_WIDTH, 50, CONSTANTS.TOP1_BACKGROUND);
    canvas.drawText('Kaikki joukkueet', 20, 12, CONSTANTS.BIG_FONT, '#000000');

    const playerTeam = getCurrentTeam(this.gameState!);
    if (playerTeam) {
      canvas.drawText(`Oma joukkue: ${playerTeam.name}`, CONSTANTS.CANVAS_WIDTH - 20, 12, CONSTANTS.MAIN_FONT, '#000066', 'right');
    }
  }

  private renderTeamDetails(canvas: Canvas): void {
    const detailX = 340;
    const detailY = 60;
    const detailWidth = CONSTANTS.CANVAS_WIDTH - detailX - 20;
    const detailHeight = 500;

    canvas.drawRect(detailX, detailY, detailWidth, detailHeight, '#f0f0f0');
    canvas.drawRectOutline(detailX, detailY, detailWidth, detailHeight, CONSTANTS.TABLE_BORDER);

    const team = this.teamList.getSelectedItem();
    if (!team) {
      canvas.drawText('Valitse joukkue', detailX + detailWidth / 2, detailY + detailHeight / 2,
        CONSTANTS.MAIN_FONT, '#666666', 'center');
      return;
    }

    // Team info header
    canvas.drawText(team.name, detailX + 15, detailY + 15, CONSTANTS.GLADIATOR_NAME_FONT, CONSTANTS.GLADIATOR_TITLE);
    canvas.drawText(`Divisioona ${team.division + 1}`, detailX + 15, detailY + 40, CONSTANTS.MAIN_FONT, '#000000');

    // Team stats
    canvas.drawText(`Voitot: ${team.wins}`, detailX + 15, detailY + 60, CONSTANTS.MSG_FONT, '#006600');
    canvas.drawText(`Tappiot: ${team.losses}`, detailX + 100, detailY + 60, CONSTANTS.MSG_FONT, '#660000');
    canvas.drawText(`Raha: ${team.money} kr`, detailX + 200, detailY + 60, CONSTANTS.MSG_FONT, '#000066');

    // Gladiators section
    canvas.drawText('Gladiaattorit:', detailX + 15, detailY + 90, CONSTANTS.MAIN_FONT, '#000000');

    if (team.gladiatorIds.length === 0) {
      canvas.drawText('Ei gladiaattoreita', detailX + 15, detailY + 115, CONSTANTS.MSG_FONT, '#666666');
      return;
    }

    const gladiatorStartY = detailY + 110;
    const gladiatorItemHeight = 55;

    for (let i = 0; i < team.gladiatorIds.length; i++) {
      const gladiator = this.gameState!.gladiators.get(team.gladiatorIds[i]);
      if (!gladiator) continue;

      const itemY = gladiatorStartY + i * gladiatorItemHeight;

      // Selection highlight
      if (i === this.selectedGladiatorIndex) {
        canvas.drawRect(detailX + 5, itemY, detailWidth - 10, gladiatorItemHeight - 3, '#d0d0ff');
      }

      // Portrait
      const portrait = this.context.assets.gladiators[gladiator.race.id];
      if (portrait) {
        canvas.drawImage(portrait, detailX + 15, itemY + 5, 40, 40);
      }

      // Name and race
      canvas.drawText(gladiator.name, detailX + 65, itemY + 5, CONSTANTS.MAIN_FONT, CONSTANTS.GLADIATOR_TITLE);
      canvas.drawText(gladiator.race.name, detailX + 65, itemY + 22, CONSTANTS.MSG_FONT, '#666666');

      // Stats
      canvas.drawText(`HP:${gladiator.hp} STR:${gladiator.str} SPD:${gladiator.speed}`,
        detailX + 65, itemY + 37, CONSTANTS.MSG_FONT, '#000066');

      // Click region
      canvas.addClickableRegion({
        id: `gladiator-${i}`,
        x: detailX + 5,
        y: itemY,
        width: detailWidth - 10,
        height: gladiatorItemHeight,
        onClick: () => {
          this.selectedGladiatorIndex = i;
        },
      });
    }
  }

  private renderBackButton(canvas: Canvas): void {
    canvas.drawButton(
      'back',
      'Takaisin',
      20,
      CONSTANTS.CANVAS_HEIGHT - 50,
      100,
      35,
      CONSTANTS.MAIN_BUTTON_FONT,
      '#ffffff',
      CONSTANTS.BUTTON_AREA_BACKGROUND,
      () => this.context.onScreenChange(ScreenState.MAIN_MENU)
    );
  }

  handleClick(x: number, y: number): void {
    console.log(`TeamsScreen click at (${x}, ${y})`);
  }

  handleKeyDown(key: string): void {
    if (key === 'Escape') {
      this.context.onScreenChange(ScreenState.MAIN_MENU);
    } else {
      const handled = this.teamList.handleKeyDown(key);
      if (handled) {
        this.selectedGladiatorIndex = 0;
      }
    }
  }

  handleWheel(x: number, y: number, deltaY: number): void {
    this.teamList.handleWheel(x, y, deltaY);
  }
}
